import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  formattedDate,
  formattedTime,
} from "../formatted/FormattedDateAndTime";
import useWindowDimensions from "../formatted/UseWindowsDimensions";
import { CropImage } from "../utils/ImageTreatment";
import axios from "axios";
import Cookies from "js-cookie";
import Loading from "../utils/Loading";
import { formattedColorMaterial } from "../formatted/FormattedText";
import { ImageCardDropdownMaterial } from "./ImageCardDropdownMaterial";
import { ImageCardDropdownSizeRange } from "./ImageCardDropdownSizeRange";
import { FaLock } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { SIZE_RANGES } from "../../shared/utils/sizeRangeUtils";
import RestoreDeletedSegmentations from "../utils/RestoreDeletedSegmentations";
import { getDeletedSegmentations } from "../../services/container";
import { getCameraId } from "../../shared/utils/utils";
import { capitalizeFirstLetter } from "../utils/Date";

export const ImageCardComponent = ({
  container,
  displayLegend,
  history,
  historyId,
  setHistoryId,
  isCharacterization,
  displayAnomalyLegend,
  isActivity,
  containerSortingError,
  containers,
  superContainer,
  proportion,
  displayAllCountours = false,
  // NEW: shows dropdown but empties its options when truedropdown
  hideMaterials = false,
  // External materials control (for parent component control)
  externalMaterialsToDisplay,
  externalMaterialList,
  externalMaterialSelectRef,
  onMaterialsToDisplayChange,
  // External size ranges control (for parent component control)
  externalSelectedSizeRanges,
  externalSizeRangeSelectRef,
  onSelectedSizeRangesChange,
  // Hide dropdown and show new objects button
  hideMaterialDropdown = false,
  hideShowNewObjectsButton = false,
  // Activity history synthesis: sorting-errors filter, no size picker
  hideSizeRangeDropdown = false,
  materialsPillLabelKey = "Materials",
  materialsSelectEmptyLabelKey = "select_materials",
  sortingErrorsMaterialsOnly = false,
  onAfterSegmentationUpdate,
  isQualicheck,
}) => {
  const { t } = useTranslation();
  const API = process.env.REACT_APP_API_BACKEND;
  const token = Cookies.get("userToken");
  const internalMaterialSelectRef = useRef();
  const materialSelectRef =
    externalMaterialSelectRef || internalMaterialSelectRef;
  const internalSizeRangeSelectRef = useRef();
  const sizeRangeSelectRef =
    externalSizeRangeSelectRef || internalSizeRangeSelectRef;

  const isAkanthasAccount = (Cookies.get("userEmail") || "").includes(
    "@akanthas.com",
  );
  const viewMode = Cookies.get("viewMode") || "client";
  const hideUnknownObjects =
    isCharacterization && !(isAkanthasAccount && viewMode === "admin");

  const { widthWindow } = useWindowDimensions();
  const [temporalData, setTemporalData] = useState([]);
  const [isLoadingTemporalData, setIsLoadingTemporalData] = useState(true);
  const [isLandscape, setIsLandscape] = useState(false);
  const [internalMaterialsToDisplay, setInternalMaterialsToDisplay] = useState(
    [],
  );
  const [internalMaterialList, setInternalMaterialList] = useState([]);

  // Use external props if provided, otherwise use internal state
  // This ensures backward compatibility: if props are not provided, behavior is identical to before
  const materialsToDisplay =
    externalMaterialsToDisplay !== undefined
      ? externalMaterialsToDisplay
      : internalMaterialsToDisplay;
  const setMaterialsToDisplay =
    onMaterialsToDisplayChange !== undefined
      ? onMaterialsToDisplayChange
      : setInternalMaterialsToDisplay;
  const materialList =
    externalMaterialList !== undefined
      ? externalMaterialList
      : internalMaterialList;
  const setMaterialList =
    externalMaterialList !== undefined
      ? () => {} // No-op if external
      : setInternalMaterialList;
  const [anomalyList, setAnomalyList] = useState([]);
  const [displayStatIgnored, setDisplayStatIgnored] = useState(false);
  const [showNewObjects, setShowNewObjects] = useState(false);
  const [
    showRestoreDeletedSegmentationsModal,
    setShowRestoreDeletedSegmentationsModal,
  ] = useState(false);
  const [
    restoreDeletedSegmentationsBlobName,
    setRestoreDeletedSegmentationsBlobName,
  ] = useState(null);
  const [hasDeletedSegmentations, setHasDeletedSegmentations] = useState(false);
  const [hasDeletedSegmentationsByContainerId, setHasDeletedSegmentationsById] =
    useState({});

  const primaryTemporalData = useMemo(() => {
    if (superContainer) {
      const primaryContainerId = containers?.[0]?._id;
      return primaryContainerId ? temporalData?.[primaryContainerId] : null;
    }
    return temporalData;
  }, [superContainer, containers, temporalData]);

  const orderedSuperContainers = useMemo(() => {
    if (
      !superContainer ||
      !Array.isArray(containers) ||
      containers.length < 2
    ) {
      return containers || [];
    }

    const getSuffix = (containerItem) => {
      const url =
        temporalData?.[containerItem?._id]?.image_url ||
        containerItem?.image_url;
      const match = typeof url === "string" ? url.match(/-(L|R)\./) : null;
      return match?.[1] || null;
    };

    const score = (suffix) => {
      if (suffix === "L") return 0;
      if (suffix === "R") return 1;
      return 2; // unknown goes last
    };

    const copy = [...containers];
    copy.sort((a, b) => score(getSuffix(a)) - score(getSuffix(b)));
    return copy;
  }, [superContainer, containers, temporalData]);

  const leftContainer = superContainer ? orderedSuperContainers[0] : null;
  const rightContainer = superContainer ? orderedSuperContainers[1] : null;

  const outputSurfaceBlobNameForRestore = useMemo(() => {
    if (!primaryTemporalData?.image_url || !primaryTemporalData?._time)
      return null;
    const cameraId = getCameraId(primaryTemporalData.image_url);
    const measurementId =
      primaryTemporalData._measurement ||
      container?._measurement ||
      container?._id;
    if (!cameraId || !measurementId) return null;
    return `camera-ak_${cameraId}/output-surface/${primaryTemporalData._time
      .replace(/:/g, "_")
      .slice(0, -1)}-camera-ak_${cameraId}-${measurementId}-surface.json`;
  }, [
    primaryTemporalData?.image_url,
    primaryTemporalData?._time,
    primaryTemporalData?._measurement,
    container,
  ]);

  const outputSurfaceBlobNameByContainerId = useMemo(() => {
    if (!superContainer || !containers?.length) {
      return {};
    }

    const blobNames = {};
    for (const containerItem of containers) {
      const temporal = temporalData?.[containerItem?._id];
      if (!temporal?.image_url || !temporal?._time) {
        continue;
      }

      const cameraId = getCameraId(temporal.image_url);
      const measurementId =
        temporal._measurement ||
        containerItem?._measurement ||
        containerItem?._id;
      if (!cameraId || !measurementId) {
        continue;
      }

      blobNames[containerItem._id] =
        `camera-ak_${cameraId}/output-surface/${temporal._time
          .replace(/:/g, "_")
          .slice(0, -1)}-camera-ak_${cameraId}-${measurementId}-surface.json`;
    }

    return blobNames;
  }, [superContainer, containers, temporalData]);

  const checkDeletedSegmentations = useCallback(async () => {
    if (!isCharacterization) {
      setHasDeletedSegmentations(false);
      setHasDeletedSegmentationsById({});
      return;
    }

    if (superContainer) {
      const entries = Object.entries(outputSurfaceBlobNameByContainerId).filter(
        ([, blobName]) => Boolean(blobName),
      );

      if (entries.length === 0) {
        setHasDeletedSegmentations(false);
        setHasDeletedSegmentationsById({});
        return;
      }

      const results = await Promise.all(
        entries.map(async ([containerId, blobName]) => {
          try {
            const response = await getDeletedSegmentations({ blobName });
            const deletedCount =
              response.data?.deletedSegmentations?.length || 0;
            return [containerId, deletedCount > 0];
          } catch (error) {
            console.error("Error checking deleted segmentations:", error);
            return [containerId, false];
          }
        }),
      );

      const byId = Object.fromEntries(results);
      setHasDeletedSegmentationsById(byId);
      setHasDeletedSegmentations(Object.values(byId).some(Boolean));
      return;
    }

    if (!outputSurfaceBlobNameForRestore) {
      setHasDeletedSegmentations(false);
      return;
    }

    try {
      const response = await getDeletedSegmentations({
        blobName: outputSurfaceBlobNameForRestore,
      });
      const deletedCount = response.data?.deletedSegmentations?.length || 0;
      setHasDeletedSegmentations(deletedCount > 0);
    } catch (error) {
      console.error("Error checking deleted segmentations:", error);
      setHasDeletedSegmentations(false);
    }
  }, [
    isCharacterization,
    superContainer,
    outputSurfaceBlobNameByContainerId,
    outputSurfaceBlobNameForRestore,
  ]);
  // Keep "total" always enabled for unknown-size objects and the "all sizes" state
  const [internalSelectedSizeRanges, setInternalSelectedSizeRanges] = useState(
    new Set(SIZE_RANGES),
  );

  // Use external props if provided, otherwise use internal state
  const selectedSizeRanges = hideSizeRangeDropdown
    ? new Set(SIZE_RANGES)
    : externalSelectedSizeRanges !== undefined
      ? externalSelectedSizeRanges
      : internalSelectedSizeRanges;
  const setSelectedSizeRanges =
    onSelectedSizeRangesChange !== undefined
      ? onSelectedSizeRangesChange
      : setInternalSelectedSizeRanges;

  const handleAnomalyList = useCallback(() => {
    if (temporalData.typeAnomaly) {
      let newAnomalyList = Object.keys(temporalData.typeAnomaly).map(
        (type) => ({
          name: `${t(type)}`,
          value: type,
          nameAndCount: `${t(type)} (${temporalData.typeAnomaly[type].count})`,
          color: formattedColorMaterial(type),
        }),
      );

      // special materials appended only if we actually have a list
      if (materialList.length > 0) {
        const specialMaterials = materialList
          .filter((material) =>
            [
              "materials_with_comment",
              "materials_with_review_cls",
              "materials_with_demand_delete",
            ].includes(material.value),
          )
          .map((material) => ({
            name: `${t(material.value)}`,
            value: material.value,
            nameAndCount: `${t(material.value)} (${material.count})`,
            color: material.color,
          }));

        if (specialMaterials.length > 0) {
          newAnomalyList = [...newAnomalyList, ...specialMaterials];
        }
      }

      setAnomalyList(newAnomalyList);
    }
  }, [temporalData.typeAnomaly, materialList, t]);

  const fetchTemporalData = useCallback(
    async (id) => {
      // Don't set loading here - it's handled by fetchAllTemporalData
      // Don't reset materials here either - this function is only for fetching data
      // Material reset should happen elsewhere if needed

      try {
        const response = await axios.get(`${API}temporalData/getOne/${id}`, {
          headers: { authorization: `Bearer ${token}` },
          params: {
            time: history[historyId]._time,
          },
        });

        let materialsTemporalData = [];

        if (response.status === 200) {
          const data = response.data.temporalData;

          if (data && data.length > 0) {
            const temporal = data[0];
            if (hideMaterials) {
              temporal.anomaliesObjects = [];
              temporal.anomaliesAnnotation = [];
            }
            setIsLandscape(temporal.w_box > temporal.h_box);

            // Only build materials aggregation when NOT hidden

            const authorizedMaterialValues = (temporal.authorizedMaterial || [])
              .map((value) =>
                typeof value === "string" ? value : value?.value,
              )
              .filter((value) => typeof value === "string")
              .map((value) => value.toLowerCase());

            temporal.anomaliesAnnotation.forEach((annotation) => {
              const cls = annotation.cls?.toLowerCase?.();
              if (!cls) return;
              if (
                hideUnknownObjects &&
                (cls === "unknown" || cls === "waste")
              ) {
                return;
              }

              const isSortingError =
                annotation.isAuthorizedMaterial !== true &&
                !authorizedMaterialValues.includes(cls) &&
                cls !== "unknown";

              if (sortingErrorsMaterialsOnly && !isSortingError) {
                return;
              }

              if (!sortingErrorsMaterialsOnly && annotation.comment) {
                const commentKey = "materials_with_comment";
                const commentIndex = materialsTemporalData.findIndex(
                  (mat) => mat.value === commentKey,
                );

                if (commentIndex === -1) {
                  materialsTemporalData.push({
                    name: t(commentKey),
                    value: commentKey,
                    color: "#000",
                    count: 1,
                    nameAndCount: `${t(commentKey)} (1)`,
                  });
                } else {
                  materialsTemporalData[commentIndex].count += 1;
                  materialsTemporalData[commentIndex].nameAndCount = `${t(
                    commentKey,
                  )} (${materialsTemporalData[commentIndex].count})`;
                }
              }

              if (!sortingErrorsMaterialsOnly && annotation.reviewCls) {
                const reviewKey = "materials_with_review_cls";
                const reviewIndex = materialsTemporalData.findIndex(
                  (mat) => mat.value === reviewKey,
                );

                if (reviewIndex === -1) {
                  materialsTemporalData.push({
                    name: t(reviewKey),
                    value: reviewKey,
                    color: "#888",
                    count: 1,
                    nameAndCount: `${t(reviewKey)} (1)`,
                  });
                } else {
                  materialsTemporalData[reviewIndex].count += 1;
                  materialsTemporalData[reviewIndex].nameAndCount = `${t(
                    reviewKey,
                  )} (${materialsTemporalData[reviewIndex].count})`;
                }
              }

              if (
                !sortingErrorsMaterialsOnly &&
                (annotation.suggestedForDeletion === true ||
                  Boolean(annotation.deleteSuggestionComment))
              ) {
                const deleteKey = "materials_with_demand_delete";
                const deleteIndex = materialsTemporalData.findIndex(
                  (mat) => mat.value === deleteKey,
                );

                if (deleteIndex === -1) {
                  materialsTemporalData.push({
                    name: t(deleteKey),
                    value: deleteKey,
                    color: "#111827",
                    count: 1,
                    nameAndCount: `${t(deleteKey)} (1)`,
                  });
                } else {
                  materialsTemporalData[deleteIndex].count += 1;
                  materialsTemporalData[deleteIndex].nameAndCount = `${t(
                    deleteKey,
                  )} (${materialsTemporalData[deleteIndex].count})`;
                }
              }

              const index = materialsTemporalData.findIndex(
                (material) => material.value === cls,
              );

              if (index === -1) {
                materialsTemporalData.push({
                  name: t(cls),
                  value: cls,
                  color: formattedColorMaterial(cls),
                  count: 1,
                  nameAndCount: `${t(cls)} (1)`,
                });
              } else {
                materialsTemporalData[index].count += 1;
                materialsTemporalData[index].nameAndCount =
                  `${t(cls)} (${materialsTemporalData[index].count})`;
              }
            });

            const priorityKeys = [
              "materials_with_review_cls",
              "materials_with_comment",
              "materials_with_demand_delete",
            ];
            const specialMaterials = [];
            materialsTemporalData = materialsTemporalData.filter((mat) => {
              if (priorityKeys.includes(mat.value)) {
                specialMaterials.push(mat);
                return false;
              }
              return true;
            });
            if (!sortingErrorsMaterialsOnly) {
              materialsTemporalData.push(...specialMaterials);
            }

            if (superContainer) {
              setTemporalData((prevState) => ({
                ...prevState,
                [id]: temporal,
              }));
            } else {
              setDisplayStatIgnored(temporal.ignoreStats);
              setTemporalData(temporal);
            }
          }
        }

        // If hidden, pretend nothing was downloaded
        return hideMaterials ? [] : materialsTemporalData;
      } catch (error) {
        console.error("Error while fetching temporal data:", error);
        return [];
      }
    },
    [
      API,
      token,
      history,
      historyId,
      superContainer,
      t,
      hideMaterials,
      sortingErrorsMaterialsOnly,
      hideUnknownObjects,
      // Don't include externalMaterialsToDisplay or onMaterialsToDisplayChange
      // as they change on every selection and would cause unnecessary re-fetches
    ],
  );

  const fetchAllTemporalData = useCallback(async () => {
    setIsLoadingTemporalData(true);

    // Always reset temporalData to ensure fresh data
    setTemporalData([]);

    // If using external materialList, still fetch temporal data for display
    // but don't update the materialList
    // Note: externalMaterialList is checked but not in dependencies
    // to avoid re-fetching when the array reference changes (even if content is same)
    const shouldUpdateMaterialList = externalMaterialList === undefined;

    if (shouldUpdateMaterialList) {
      setInternalMaterialList([]);
    }

    const allMaterials = [];

    try {
      if (superContainer) {
        const promises = containers.map((item) => fetchTemporalData(item._id));
        const results = await Promise.all(promises);
        results.forEach((materials) => {
          materials.forEach((mat) => {
            const index = allMaterials.findIndex((m) => m.name === mat.name);
            if (index === -1) {
              allMaterials.push({ ...mat });
            } else {
              allMaterials[index].count += mat.count;
              allMaterials[index].nameAndCount =
                `${mat.name} (${allMaterials[index].count})`;
            }
          });
        });
      } else {
        const materials = await fetchTemporalData(container._id);
        materials.forEach((mat) => allMaterials.push(mat));
      }

      // Key behavior: dropdown visible but empty list when hidden
      // Only set if not using external materialList
      if (shouldUpdateMaterialList) {
        setInternalMaterialList(hideMaterials ? [] : allMaterials);
      }
    } catch (error) {
      console.error("Error in fetchAllTemporalData:", error);
    } finally {
      // Always set loading to false after fetching, even if using external materialList
      // The actual temporalData will be set by fetchTemporalData calls
      // For superContainer, we need to wait for all data to be loaded
      if (!superContainer) {
        setIsLoadingTemporalData(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchTemporalData,
    superContainer,
    containers,
    container?._id,
    hideMaterials,
    // Don't include externalMaterialList - it changes reference on every render
    // even if content is the same, causing unnecessary re-fetches
  ]);

  useEffect(() => {
    // Always fetch temporal data for display, even if using external materialList
    fetchAllTemporalData();
    // eslint-disable-next-line
  }, [fetchAllTemporalData]);

  useEffect(() => {
    checkDeletedSegmentations();
  }, [checkDeletedSegmentations]);

  useEffect(() => {
    handleAnomalyList();
  }, [handleAnomalyList]);

  // Reset selected materials when changing image/history.
  useEffect(() => {
    materialSelectRef.current?.resetSelectedValues?.();
    setMaterialsToDisplay([]);
  }, [historyId, setMaterialsToDisplay, materialSelectRef]);

  // If we’re aggregating superContainer items, wait until we have >1 key
  useEffect(() => {
    if (Object.keys(temporalData).length > 1) {
      setIsLoadingTemporalData(false);
    }
  }, [temporalData]);

  // Ensure UI state is cleared when hideMaterials flips on
  useEffect(() => {
    if (hideMaterials && externalMaterialList === undefined) {
      materialSelectRef.current?.resetSelectedValues?.();
      setMaterialsToDisplay([]);
      if (setMaterialList) {
        setMaterialList([]); // keep dropdown visible but empty
      }
    }
    // eslint-disable-next-line
  }, [hideMaterials, externalMaterialList]);

  const getImageWidth = (data = null) => {
    // For superContainer, calculate isLandscape based on the specific container's data
    const containerIsLandscape = data ? data.w_box > data.h_box : isLandscape;

    if (containerIsLandscape) {
      if (isActivity) {
        // Wider stage so the main photo fills more of the row (less side letterboxing).
        return widthWindow / 2.5;
      } else if (containerSortingError) {
        return widthWindow / 3.2;
      } else if (superContainer) {
        return widthWindow / 2.5;
      } else {
        return widthWindow / 2;
      }
    } else {
      return ((widthWindow / 2) * 66) / 100;
    }
  };

  const getImageHeight = () => {
    // Keep height aligned with width for activity to avoid large white letterboxing.
    return isActivity ? widthWindow / 2.8 : widthWindow / 3;
  };

  const imageWidth = getImageWidth();
  const imageHeight = getImageHeight();
  // Same stage for L/R so CropImage can contain (no custom aspect = no bad crop).
  const superContainerImageWidth = superContainer
    ? Math.min(widthWindow / 2.5, Math.max(widthWindow / 2 - 24, 280))
    : imageWidth;
  const numberOfElements = history.length;

  // Dropdown controls the size range selection

  return (
    <section className="relative flex flex-col">
      {isCharacterization && !hideMaterialDropdown && (
        <section className={`flex w-full h-24 justify-center gap-2`}>
          <div
            className="flex items-center gap-2 border border-[#d3d3d3] rounded-full px-4 bg-white h-10 text-[14px] relative my-auto"
            style={{ overflow: "visible" }}
          >
            <span className="text-[#222222] whitespace-nowrap select-none font-medium">
              {t(materialsPillLabelKey)}
            </span>
            <span className="text-[#5e5e5e] select-none">:</span>
            <div
              className="flex-1 min-w-48 relative"
              style={{ overflow: "visible", zIndex: 50 }}
            >
              <ImageCardDropdownMaterial
                materialList={[...materialList].sort((a, b) =>
                  a.name.localeCompare(b.name, "fr", { sensitivity: "base" }),
                )}
                materialSelectRef={materialSelectRef}
                materialsToDisplay={materialsToDisplay}
                setMaterialsToDisplay={setMaterialsToDisplay}
                emptySelectionLabelKey={materialsSelectEmptyLabelKey}
              />
            </div>
          </div>
          {!hideSizeRangeDropdown ? (
            <div
              className="flex items-center gap-2 border border-[#d3d3d3] rounded-full px-4 bg-white h-10 text-[14px] relative my-auto"
              style={{ overflow: "visible" }}
            >
              <span className="text-[#222222] whitespace-nowrap select-none font-medium">
                {t("Size")}
              </span>
              <span className="text-[#5e5e5e] select-none">:</span>
              <div
                className="flex-1 min-w-48 relative"
                style={{ overflow: "visible", zIndex: 50 }}
              >
                <ImageCardDropdownSizeRange
                  sizeRanges={SIZE_RANGES}
                  selectedSizeRanges={selectedSizeRanges}
                  setSelectedSizeRanges={setSelectedSizeRanges}
                  sizeRangeSelectRef={sizeRangeSelectRef}
                />
              </div>
            </div>
          ) : null}
        </section>
      )}

      {isCharacterization && !hideShowNewObjectsButton && (
        <div className="absolute top-7 right-3 z-50 flex flex-col modal-item-key-value pointer-events-auto">
          {isAkanthasAccount && viewMode === "admin" && (
            <button
              className="bg-[#00A7A6] hover:bg-[#009493] text-white font-medium rounded-lg px-4 py-2 
                        text-center shadow-sm transition-colors duration-200"
              onClick={() => setShowNewObjects(!showNewObjects)}
            >
              {showNewObjects ? t("Hide new objects") : t("Show new objects")}
            </button>
          )}
          {superContainer ? (
            <>
              {containers?.slice(0, 2).map((containerItem, index) => {
                const containerId = containerItem?._id;
                if (!containerId) return null;

                const blobName =
                  outputSurfaceBlobNameByContainerId[containerId];
                const canRestore = Boolean(
                  hasDeletedSegmentationsByContainerId[containerId] && blobName,
                );
                if (!canRestore) return null;

                const sideLabel = index === 0 ? t("Left") : t("Right");

                return (
                  <button
                    key={containerId}
                    type="button"
                    className="bg-[#00A7A6] hover:bg-[#009493] text-white font-medium rounded-lg px-4 py-2 
                              text-center shadow-sm transition-colors duration-200 mt-2 flex items-center justify-center gap-2"
                    onClick={() => {
                      setRestoreDeletedSegmentationsBlobName(blobName);
                      setShowRestoreDeletedSegmentationsModal(true);
                    }}
                  >
                    <i className="fas fa-history text-sm"></i>
                    {t("Restore deleted segmentations")} ({sideLabel})
                  </button>
                );
              })}
            </>
          ) : (
            hasDeletedSegmentations && (
              <button
                type="button"
                className="bg-[#00A7A6] hover:bg-[#009493] text-white font-medium rounded-lg px-4 py-2 
                          text-center shadow-sm transition-colors duration-200 mt-2 flex items-center justify-center gap-2"
                onClick={() => {
                  setRestoreDeletedSegmentationsBlobName(
                    outputSurfaceBlobNameForRestore,
                  );
                  setShowRestoreDeletedSegmentationsModal(true);
                }}
              >
                <i className="fas fa-history text-sm"></i>
                {t("Restore deleted segmentations")}
              </button>
            )
          )}
        </div>
      )}

      {/* Navigation Button */}
      {history.length > 1 && history[historyId] !== undefined && (
        <div className="visual-card visual-card-navigation flex w-full items-center justify-center gap-24 py-2">
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              setHistoryId(Math.max(0, historyId - 1));
            }}
            disabled={historyId === 0}
            aria-label={t("Previous")}
            className={`inline-flex items-center justify-center w-7 h-7 p-0 leading-none rounded-full bg-white border border-gray-300 shrink-0 ${
              historyId === 0
                ? "opacity-40 cursor-default"
                : "hover:bg-gray-50 cursor-pointer"
            }`}
          >
            <svg
              width="8"
              height="12"
              viewBox="0 0 8 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
              className="block"
            >
              <path
                d="M7 1L2 6L7 11"
                stroke="#6B7280"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div className="flex flex-col items-center gap-1">
            <span className="text-base font-bold text-[#222222] leading-tight">
              {`${capitalizeFirstLetter(t("record"))} nº ${numberOfElements - historyId}`}
            </span>
            <p className="data-bold text-xs m-0">
              {`${formattedDate(history[historyId]._time, t)} ${t("à")} ${formattedTime(history[historyId]._time)}`}
            </p>
          </div>
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              setHistoryId(Math.min(historyId + 1, numberOfElements - 1));
            }}
            disabled={historyId === numberOfElements - 1}
            aria-label={t("Next")}
            className={`inline-flex items-center justify-center w-7 h-7 p-0 leading-none rounded-full bg-white border border-gray-300 shrink-0 ${
              historyId === numberOfElements - 1
                ? "opacity-40 cursor-default"
                : "hover:bg-gray-50 cursor-pointer"
            }`}
          >
            <svg
              width="8"
              height="12"
              viewBox="0 0 8 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
              className="block"
            >
              <path
                d="M1 1L6 6L1 11"
                stroke="#6B7280"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Image */}
      {isLoadingTemporalData || Object.keys(temporalData).length < 1 ? (
        <Loading height={`${imageHeight}px`} />
      ) : (
        <div className="w-full min-w-0">
          {isCharacterization && (
            <RestoreDeletedSegmentations
              isOpen={showRestoreDeletedSegmentationsModal}
              toggle={() =>
                setShowRestoreDeletedSegmentationsModal((isOpen) => {
                  if (isOpen) {
                    setRestoreDeletedSegmentationsBlobName(null);
                  }
                  return !isOpen;
                })
              }
              blobName={restoreDeletedSegmentationsBlobName}
              onRestore={() => {
                fetchAllTemporalData();
                checkDeletedSegmentations();
                if (typeof onAfterSegmentationUpdate === "function") {
                  onAfterSegmentationUpdate();
                }
              }}
            />
          )}
          {superContainer ? (
            <div className="flex flex-col w-full">
              <div className="flex flex-row w-full items-start justify-center gap-2">
                <div className="flex flex-1 flex-col items-center min-w-0">
                  <CropImage
                    container={leftContainer}
                    data={
                      leftContainer ? temporalData[leftContainer._id] : null
                    }
                    displayLegend={displayLegend}
                    imgDivWidth={superContainerImageWidth}
                    imgDivHeight={imageHeight}
                    isTooltip={false}
                    isMap={false}
                    isCharacterization={isCharacterization}
                    materialsDropdownToDisplay={materialsToDisplay}
                    showCheckbox={false}
                    displayAnomalyLegend={
                      anomalyList.length > 0 && displayAnomalyLegend
                    }
                    anomalyList={anomalyList}
                    isActivity={isActivity}
                    history={history}
                    containerSortingError={containerSortingError}
                    {...(!displayAllCountours &&
                      leftContainer?._id &&
                      temporalData[leftContainer._id]?.anomaliesObjects !==
                        undefined && {
                        anomaliesObjects:
                          temporalData[leftContainer._id].anomaliesObjects,
                      })}
                    fetchAllTemporalData={fetchAllTemporalData}
                    onAfterSegmentationUpdate={onAfterSegmentationUpdate}
                    disableMaterialsOnHover={hideMaterials}
                    showNewObjects={showNewObjects}
                    setShowNewObjects={setShowNewObjects}
                    selectedSizeRanges={selectedSizeRanges}
                    isQualicheck={isQualicheck}
                    hideUnknownObjects={hideUnknownObjects}
                  />
                  <p style={{ marginTop: "10px", textAlign: "center" }}>
                    {isAkanthasAccount && viewMode === "admin" ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "10px",
                        }}
                      >
                        <FaLock />
                        <span>
                          {" Left proportion: " +
                            (leftContainer &&
                            proportion?.[leftContainer._id] != null
                              ? (100 * proportion[leftContainer._id]).toFixed(1)
                              : "0.0") +
                            "%"}
                        </span>
                      </div>
                    ) : (
                      ""
                    )}
                  </p>
                </div>
                <div className="flex flex-1 flex-col items-center min-w-0">
                  <CropImage
                    container={rightContainer}
                    data={
                      rightContainer ? temporalData[rightContainer._id] : null
                    }
                    displayLegend={displayLegend}
                    imgDivWidth={superContainerImageWidth}
                    imgDivHeight={imageHeight}
                    isTooltip={false}
                    isMap={false}
                    isCharacterization={isCharacterization}
                    materialsDropdownToDisplay={materialsToDisplay}
                    showCheckbox={false}
                    displayAnomalyLegend={
                      anomalyList.length > 0 && displayAnomalyLegend
                    }
                    anomalyList={anomalyList}
                    isActivity={isActivity}
                    history={history}
                    containerSortingError={containerSortingError}
                    {...(!displayAllCountours &&
                      rightContainer?._id &&
                      temporalData[rightContainer._id]?.anomaliesObjects !==
                        undefined && {
                        anomaliesObjects:
                          temporalData[rightContainer._id].anomaliesObjects,
                      })}
                    fetchAllTemporalData={fetchAllTemporalData}
                    onAfterSegmentationUpdate={onAfterSegmentationUpdate}
                    disableMaterialsOnHover={hideMaterials}
                    showNewObjects={showNewObjects}
                    setShowNewObjects={setShowNewObjects}
                    selectedSizeRanges={selectedSizeRanges}
                    isQualicheck={isQualicheck}
                    hideUnknownObjects={hideUnknownObjects}
                  />
                  <p style={{ marginTop: "10px", textAlign: "center" }}>
                    {isAkanthasAccount && viewMode === "admin" ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "10px",
                        }}
                      >
                        <FaLock />
                        <span>
                          {" Right proportion: " +
                            (rightContainer &&
                            proportion?.[rightContainer._id] != null
                              ? (100 * proportion[rightContainer._id]).toFixed(
                                  1,
                                )
                              : "0.0") +
                            "%"}
                        </span>
                      </div>
                    ) : (
                      ""
                    )}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <CropImage
              container={container}
              data={temporalData}
              displayLegend={displayLegend}
              imgDivWidth={imageWidth}
              imgDivHeight={imageHeight}
              isTooltip={false}
              isMap={false}
              isCharacterization={isCharacterization}
              materialsDropdownToDisplay={materialsToDisplay}
              showCheckbox={false}
              displayAnomalyLegend={
                anomalyList.length > 0 && displayAnomalyLegend
              }
              anomalyList={anomalyList}
              isActivity={isActivity}
              history={history}
              containerSortingError={containerSortingError}
              {...(!displayAllCountours &&
                temporalData?.anomaliesObjects !== undefined && {
                  anomaliesObjects: temporalData.anomaliesObjects,
                })}
              displayStatIgnored={displayStatIgnored}
              fetchAllTemporalData={fetchAllTemporalData}
              onAfterSegmentationUpdate={onAfterSegmentationUpdate}
              disableMaterialsOnHover={hideMaterials}
              showNewObjects={showNewObjects}
              setShowNewObjects={setShowNewObjects}
              selectedSizeRanges={selectedSizeRanges}
              isQualicheck={isQualicheck}
              hideUnknownObjects={hideUnknownObjects}
            />
          )}
        </div>
      )}
    </section>
  );
};
